import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { LEAD_SOURCES, LEAD_SERVICES, LEAD_PRIORITIES, LEAD_STATUSES } from "./leadUtils";
import { Checkbox } from "../ui/checkbox";
import AttachmentPicker from "../project/AttachmentPicker";
import CurrencySelect from "../CurrencySelect";
import { canSeeCosts } from "../../lib/services";

const empty = {
  lead_name: "", company: "", contact_person: "", phone: "", email: "", website: "",
  lead_source: "", service_required: [], lead_value: "", currency: "USD",
  assigned_to: "", head_id: "",
  lead_date: new Date().toISOString().slice(0,10),
  priority: "Medium", status: "New",
  next_followup_date: "", last_contact_date: "", notes: "", attachments: [],
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+?[0-9\s\-()]{7,20}$/;

function validateLead(f, showCosts) {
  const e = {};
  if (!f.lead_name.trim()) e.lead_name = "Lead name is required.";
  if (!f.contact_person.trim()) e.contact_person = "Contact person is required.";
  if (!f.phone.trim()) e.phone = "Phone number is required.";
  else if (!PHONE_RE.test(f.phone.trim()) || f.phone.replace(/\D/g, "").length < 7) e.phone = "Please enter a valid phone number.";
  if (!f.email.trim()) e.email = "Email is required.";
  else if (!EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid email address.";
  if (!f.lead_source) e.lead_source = "Lead source is required.";
  if (!f.service_required.length) e.service_required = "Pick at least one service.";
  if (!showCosts) { /* value hidden */ }
  else if (f.lead_value === "" || f.lead_value === null) e.lead_value = "Lead value is required.";
  else if (!(Number(f.lead_value) > 0)) e.lead_value = "Amount must be greater than 0.";
  if (!f.lead_date) e.lead_date = "Lead date is required.";
  if (f.next_followup_date && f.lead_date && f.next_followup_date < f.lead_date) e.next_followup_date = "Follow-up date cannot be earlier than the lead date.";
  if (f.last_contact_date && f.lead_date && f.last_contact_date < f.lead_date) e.last_contact_date = "Last contact cannot be earlier than the lead date.";
  return e;
}

export default function LeadForm({ open, onOpenChange, initial, users = [], currentUser, onSaved }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const showCosts = canSeeCosts(currentUser);

  useEffect(() => {
    if (open) {
      setErrors({});
      const src = initial ? { ...empty, ...initial } : { ...empty, assigned_to: currentUser?.role === "sales_member" ? currentUser.id : "" };
      // Coerce legacy string service_required to list
      if (typeof src.service_required === "string") {
        src.service_required = src.service_required ? [src.service_required] : [];
      } else if (!Array.isArray(src.service_required)) {
        src.service_required = [];
      }
      setForm(src);
    }
  }, [open, initial, currentUser]);

  const toggleService = (s) => setForm(f => ({
    ...f,
    service_required: f.service_required.includes(s)
      ? f.service_required.filter(x => x !== s)
      : [...f.service_required, s],
  }));

  const heads = users.filter(u => ["sales_head", "ceo", "admin", "manager"].includes(u.role));
  const owners = users.filter(u => ["sales_head", "sales_member", "ceo", "admin", "manager"].includes(u.role));

  const save = async () => {
    const errs = validateLead(form, showCosts);
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error(Object.values(errs)[0]); return; }
    setBusy(true);
    try {
      const payload = { ...form, lead_value: Number(form.lead_value), phone: form.phone.trim(), email: form.email.trim() };
      if (!showCosts) delete payload.lead_value;
      if (initial?.id) await api.put(`/leads/${initial.id}`, payload);
      else await api.post("/leads", payload);
      toast.success(initial ? "Lead updated" : "Lead created");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{initial ? "Edit Lead" : "Add Lead"}</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">Capture the opportunity so no revenue slips through.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <F label="Lead Name" required error={errors.lead_name}><Input value={form.lead_name} onChange={e => setForm({...form, lead_name: e.target.value})} data-testid="lf-name" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Company"><Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} data-testid="lf-company" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Contact Person" required error={errors.contact_person}><Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} data-testid="lf-contact" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Phone" required error={errors.phone}><Input type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="lf-phone" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Email" required error={errors.email}><Input type="email" placeholder="name@company.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="lf-email" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Website"><Input value={form.website} onChange={e => setForm({...form, website: e.target.value})} data-testid="lf-website" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Lead Source" required error={errors.lead_source}>
            <Select value={form.lead_source} onValueChange={v => setForm({...form, lead_source: v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="lf-source"><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{LEAD_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label={`Service Required${form.service_required.length ? ` (${form.service_required.length})` : ""}`} span={2} required error={errors.service_required}>
            <div className="rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] p-2 flex flex-wrap gap-1.5" data-testid="lf-service">
              {LEAD_SERVICES.map(s => {
                const on = form.service_required.includes(s);
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => toggleService(s)}
                    data-testid={`lf-service-${s.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                    className={`px-2.5 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-wider border transition-colors ${on ? "bg-orange-100 text-orange-700 border-orange-500/40" : "bg-[#FFFFFF] text-neutral-600 border-[#E4E7EC] hover:text-neutral-900"}`}>
                    {on && <span className="mr-1">✓</span>}{s}
                  </button>
                );
              })}
            </div>
          </F>
          {showCosts && <>
          <F label="Currency"><CurrencySelect value={form.currency} onChange={v => setForm({...form, currency: v})} testid="lf-currency"/></F>
          <F label="Lead Value" required error={errors.lead_value}><Input type="number" min="0.01" step="any" placeholder="e.g. 50000" value={form.lead_value} onChange={e => setForm({...form, lead_value: e.target.value})} data-testid="lf-value" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          </>}
          <F label="Priority">
            <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="lf-priority"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{LEAD_PRIORITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Assigned To">
            <Select value={form.assigned_to || "none"} onValueChange={v => setForm({...form, assigned_to: v === "none" ? "" : v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="lf-assign"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                <SelectItem value="none">— Unassigned —</SelectItem>
                {owners.map(u => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-neutral-500 text-[10px] ml-1">({u.role})</span></SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Head">
            <Select value={form.head_id || "none"} onValueChange={v => setForm({...form, head_id: v === "none" ? "" : v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="lf-head"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                <SelectItem value="none">— None —</SelectItem>
                {heads.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Lead Date" required error={errors.lead_date}><Input type="date" value={form.lead_date} onChange={e => setForm({...form, lead_date: e.target.value})} data-testid="lf-date" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Last Contact" error={errors.last_contact_date}><Input type="date" min={form.lead_date || undefined} value={form.last_contact_date || ""} onChange={e => setForm({...form, last_contact_date: e.target.value})} data-testid="lf-last-contact" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Next Follow-up Date" error={errors.next_followup_date}><Input type="date" min={form.lead_date || undefined} value={form.next_followup_date} onChange={e => setForm({...form, next_followup_date: e.target.value})} data-testid="lf-followup" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Status">
            <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="lf-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{LEAD_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Notes" span={2}><Textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="lf-notes" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Attachments" span={2}><AttachmentPicker value={form.attachments} onChange={a => setForm({...form, attachments: a})} testid="lf-att" /></F>
        </div>
        <div className="text-[10px] font-mono text-neutral-500"><span className="text-rose-500">*</span> Mandatory fields</div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="lf-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="lf-save">
            {busy ? "Saving..." : (initial ? "Save Changes" : "Add Lead")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({label, required, error, children, span = 1}) {
  return (
    <div className={`space-y-1.5 ${span === 2 ? "col-span-2" : ""}`}>
      <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </Label>
      {children}
      {error && <div className="text-[11px] text-rose-600" data-testid="field-error">{error}</div>}
    </div>
  );
}
